import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, AlertTriangle, ArrowDown, Loader2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { formatDate, formatDateTime } from "../../lib/date";
import { STATUS_TONE } from "./prospects/prospectConstants";
import { formatMoney0 } from "../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [
  { key: "this_month", label: "This month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_90_days", label: "Last 90 days" },
  { key: "rolling_12_months", label: "Rolling 12 months" },
];
const money = (v, cur = "AUD") => formatMoney0(v, cur);
const money2 = (v, cur = "AUD") => formatMoney0(v, cur);

function OutcomePill({ status }) {
  const t = STATUS_TONE[status];
  if (!t || t.quiet) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="rounded-full" style={{ width: 5, height: 5, background: "#8A857A" }} />
        <span className="text-[12px] text-[#59554D]">{t?.label || (status || "—").replace(/_/g, " ")}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" style={{ background: t.fill, color: t.ink, border: `1px solid ${t.border}`, borderRadius: 999, padding: "2px 9px" }}>
      <span className="rounded-full" style={{ width: 5, height: 5, background: t.dot }} />
      <span className="text-[10.5px] font-bold tracking-[0.04em]">{t.label}</span>
    </span>
  );
}

function ConvRow({ rate, text, weakest }) {
  if (rate == null) return null;
  return (
    <div className="flex items-center gap-2 pl-0.5 py-1.5">
      <ArrowDown className="w-3.5 h-3.5 text-[#8A857A]" />
      <span className="mono text-[12.5px] font-bold" style={{ color: weakest ? "#9A3200" : "#1A1A1A" }}>{rate}%</span>
      <span className="text-[12px] text-[#59554D]">{text}</span>
    </div>
  );
}

function FunnelStage({ label, count, base, tone }) {
  const pct = base ? Math.max((count / base) * 100, count > 0 ? 4 : 0) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-semibold text-[#1A1A1A] mb-1">{label}</div>
        <div className="rounded-[5px] overflow-hidden" style={{ height: 18, background: "#F4F2ED", border: "1px solid #E6E2D9" }}>
          <div className="h-full rounded-[5px]" style={{ width: `${pct}%`, minWidth: count > 0 ? 6 : 0, background: tone }} />
        </div>
      </div>
      <div className="text-right shrink-0" style={{ width: 54 }}>
        <span className="text-[20px] font-extrabold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>{count}</span>
      </div>
    </div>
  );
}

function RevenueBar({ label, booked, out }) {
  const total = booked + out;
  const pct = total ? Math.round((booked / total) * 100) : 0;
  const bookedW = total ? (booked / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12.5px] font-semibold text-[#1A1A1A]">{label}</span>
        <span className="mono text-[11.5px] text-[#59554D]">{pct}% booked</span>
      </div>
      <div className="rounded-[5px] overflow-hidden flex" style={{ height: 26, border: "1px solid #DAD5CA" }}>
        <div style={{ width: `${bookedW}%`, background: "#0E6B3F" }} />
        <div style={{ width: `${100 - bookedW}%`, background: "#E0B800" }} />
      </div>
      <div className="flex items-center gap-4 mt-2">
        <span className="inline-flex items-center gap-1.5"><span className="rounded-full" style={{ width: 6, height: 6, background: "#0E6B3F" }} /><span className="text-[11.5px] text-[#59554D]">Booked</span><span className="mono text-[12.5px] font-bold text-[#1A1A1A]">{money(booked)}</span></span>
        <span className="inline-flex items-center gap-1.5"><span className="rounded-full" style={{ width: 6, height: 6, background: "#E0B800" }} /><span className="text-[11.5px] text-[#59554D]">Still out</span><span className="mono text-[12.5px] font-bold text-[#1A1A1A]">{money(out)}</span></span>
      </div>
    </div>
  );
}

export default function SalesOverview() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => localStorage.getItem("equipr_sales_period") || "last_90_days");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/sales/overview`, { params: { period } });
      setData(r.data);
    } catch (e) { setError(true); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const changePeriod = (p) => { setPeriod(p); localStorage.setItem("equipr_sales_period", p); setShowAll(false); };

  if (loading && !data) return <div className="flex items-center gap-2 text-[#8A857A] p-6" data-testid="so-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading overview…</div>;
  if (error && !data) return <div className="p-6"><p className="text-[15px] font-semibold text-[#1A1A1A]">Couldn't load the sales overview.</p><Button variant="outline" onClick={load} className="mt-3 border-[#C9C3B6] bg-white text-[#59554D]">Retry</Button></div>;

  const h = data.headline, f = data.funnel, rev = data.revenue;
  const rates = [f.r_sent, f.r_accepted, f.r_converted].filter((x) => x != null);
  const weakest = rates.length ? Math.min(...rates) : null;
  const funnelBase = Math.max(f.demos_completed, f.quote_sent, f.quote_accepted, f.converted, 1);
  const demos = showAll ? data.demos : data.demos.slice(0, 5);

  const cell = (over, value, unit, sub, chip, fx) => (
    <div className="bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">{over}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[26px] font-extrabold text-[#1A1A1A] tracking-[-0.02em]" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</span>
        {unit && <span className="text-[12.5px] text-[#59554D]">{unit}</span>}
      </div>
      <div className="text-[12.5px] text-[#59554D] mt-1.5 flex items-center gap-1.5 flex-wrap">
        {chip}{sub}
      </div>
      {fx ? <div className="text-[11.5px] mt-1 font-semibold" style={{ color: "#6B5600" }} data-testid="so-fx">plus {fx}</div> : null}
    </div>
  );

  /* Foreign-currency figures sit beside the AUD headline — never summed into it. */
  const fxLine = (key, suffix = "") => {
    const split = (data.by_currency || {})[key] || {};
    return Object.entries(split).filter(([c]) => c !== "AUD" && split[c])
      .map(([c, v]) => `${money(v, c)}${suffix}`).join(" · ");
  };
  const deltaChip = (v) => (v == null || v === 0) ? null : (
    <span className="mono text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: v > 0 ? "#E3F3E9" : "#FFE9E0", color: v > 0 ? "#0B4F2E" : "#9A3200" }}>
      {v > 0 ? "+" : "−"}{money(Math.abs(v))}
    </span>
  );
  const pctChip = (v) => (v == null || v === 0) ? null : (
    <span className="mono text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: v > 0 ? "#E3F3E9" : "#FFE9E0", color: v > 0 ? "#0B4F2E" : "#9A3200" }}>
      {v > 0 ? "+" : "−"}{Math.abs(v)} pts
    </span>
  );
  const subCol = (title, col) => {
    const maxc = Math.max(1, ...col.items.map((i) => i.count), col.unset);
    return (
      <div className="bg-white p-4">
        <div className="flex items-baseline justify-between mb-2.5">
          <span className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-bold">{title}</span>
          <span className="mono text-[11.5px] text-[#59554D]">{col.total} open</span>
        </div>
        {col.total === 0 ? (
          <div className="text-[12.5px] text-[#B5AFA3]">No open prospects in this stage.</div>
        ) : (
          <div className="space-y-1.5">
            {col.items.map((it) => (
              <div key={it.key} className="flex items-center gap-2" data-testid={`so-sub-${it.key}`}>
                <span className="text-[12px] text-[#59554D] w-[172px] shrink-0 truncate" title={it.label}>{it.label}</span>
                <div className="flex-1 h-[16px] bg-[#F4F2ED] rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${(it.count / maxc) * 100}%`, background: it.count ? "#E0B800" : "transparent" }} />
                </div>
                <span className="mono text-[12px] font-bold text-[#1A1A1A] w-[26px] text-right">{it.count}</span>
              </div>
            ))}
            {col.unset > 0 && (
              <div className="flex items-center gap-2 pt-1" data-testid="so-sub-unset">
                <span className="text-[12px] text-[#B5AFA3] italic w-[172px] shrink-0 truncate">No sub-status set</span>
                <div className="flex-1 h-[16px] bg-[#F4F2ED] rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${(col.unset / maxc) * 100}%`, background: "#D8D2C6" }} />
                </div>
                <span className="mono text-[12px] font-bold text-[#8A857A] w-[26px] text-right">{col.unset}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3.5" data-testid="sales-overview" id="sales-overview-print">
      <style>{`@media print { body * { visibility: hidden !important; } #sales-overview-print, #sales-overview-print * { visibility: visible !important; } #sales-overview-print { position: absolute; left: 0; top: 0; width: 100%; padding: 12px; } .so-no-print { display: none !important; } }`}</style>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[25px] font-extrabold text-[#1A1A1A] tracking-[-0.018em]" style={{ fontFamily: "Manrope, sans-serif" }}>Sales overview</h1>
          <p className="text-[13px] text-[#59554D] mt-0.5">
            {data.period_label}, <span className="mono text-[11.5px] text-[#1A1A1A]">{formatDate(data.start)}</span> to <span className="mono text-[11.5px] text-[#1A1A1A]">{formatDate(data.end)}</span>. Figures as at <span className="mono text-[11.5px] text-[#1A1A1A]">{formatDateTime(data.as_at).split(" ")[1]}</span> today.
          </p>
        </div>
        <div className="flex items-center gap-2.5 so-no-print">
          <div className="inline-flex items-center rounded-full p-0.5" style={{ background: "#F4F2ED", border: "1px solid #DAD5CA" }}>
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => changePeriod(p.key)} data-testid={`so-period-${p.key}`}
                className={`h-7 px-3 rounded-full text-[12px] transition-colors ${period === p.key ? "bg-white text-[#1A1A1A] font-semibold" : "text-[#59554D] hover:text-[#1A1A1A]"}`}
                style={period === p.key ? { border: "1px solid #E0B800" } : undefined}>{p.label}</button>
            ))}
          </div>
          <Button onClick={() => window.print()} data-testid="so-export" className="h-[34px] bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[12px] tracking-[0.04em] font-bold" style={{ border: "1px solid #E0B800" }}>
            <Download className="w-4 h-4 mr-2" /> Export summary
          </Button>
        </div>
      </div>

      {/* Headline band */}
      <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", background: "#E6E2D9", gap: 1 }}>
            {cell("Recurring revenue booked", money(h.recurring_booked), "per month", `across ${h.accepted_count} accepted quotes`, deltaChip(h.recurring_delta), fxLine("recurring_booked", " /mo"))}
            {cell("One-off revenue booked", money(h.one_off_booked), null, "implementation and setup", deltaChip(h.one_off_delta), fxLine("one_off_booked"))}
            {cell("Still out with prospects", money(h.recurring_out), "per month", `plus ${money(h.one_off_out)} one-off, across ${h.sent_count} sent quotes`, null, fxLine("recurring_out", " /mo"))}
            {cell("Demo to client", `${h.demo_to_client_pct}%`, null, `${h.converted} of ${h.demos_completed} tracked prospects converted; ${h.undecided} undecided`, null)}
            {data.win_rate?.measurable && cell("Quote win rate", `${data.win_rate.rate}%`, null, `${data.win_rate.accepted} of ${data.win_rate.decided} decided quotes won · ${data.win_rate.lost} lost`, pctChip(data.win_rate.delta))}
          </div>
        </CardContent>
      </Card>

      {/* Middle row */}
      <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", alignItems: "start" }}>
        {/* After the demo */}
        <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-[#E6E2D9]">
              <div className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>After the demo</div>
              <div className="text-[11.5px] text-[#59554D] mt-0.5">Every prospect who completed a demo in the window — plus anyone whose quote was accepted or who converted in it — and where they are now.</div>
            </div>
            <div className="p-4 space-y-0">
              <FunnelStage label="Demos completed" count={f.demos_completed} base={funnelBase} tone="#8A857A" />
              <ConvRow rate={f.r_sent} text="went on to receive a quote" weakest={f.r_sent === weakest} />
              <FunnelStage label="Quote sent" count={f.quote_sent} base={funnelBase} tone="#E0B800" />
              <ConvRow rate={f.r_accepted} text="of quotes sent have been accepted" weakest={f.r_accepted === weakest} />
              <FunnelStage label="Quote accepted" count={f.quote_accepted} base={funnelBase} tone="#0E6B3F" />
              <ConvRow rate={f.r_converted} text="of accepted quotes became clients" weakest={f.r_converted === weakest} />
              <FunnelStage label="Converted to client" count={f.converted} base={funnelBase} tone="#0E6B3F" />
            </div>
            {f.undecided > 0 && (
              <div className="mx-4 mb-4 rounded-lg px-3.5 py-2.5 flex items-start gap-2" style={{ background: "#FFF7D1", border: "1px solid #E8D48A" }}>
                <span className="rounded-full mt-1.5 shrink-0" style={{ width: 5, height: 5, background: "#E0B800" }} />
                <p className="text-[12.5px]" style={{ color: "#6B5600" }}>
                  <span className="font-bold">{f.undecided} of {f.demos_completed} tracked prospects have not decided.</span> Nothing in the window has been marked lost, so the pipeline neither closes nor clears.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue booked and open */}
        <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-[#E6E2D9]">
              <div className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Revenue booked and open</div>
              <div className="text-[11.5px] text-[#59554D] mt-0.5">Accepted quotes against quotes still out.</div>
            </div>
            <div className="p-4 space-y-4">
              <RevenueBar label="Recurring, per month" booked={rev.recurring_booked} out={rev.recurring_out} />
              <RevenueBar label="One-off" booked={rev.one_off_booked} out={rev.one_off_out} />
            </div>
            <div className="px-4 py-3 border-t border-[#F0EDE6]">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Average accepted quote</div>
              {rev.avg_available ? (
                <div className="text-[#1A1A1A]"><span className="mono text-[16px] font-bold">{money(rev.avg_monthly)}</span><span className="text-[12.5px] text-[#59554D]"> per month, plus </span><span className="mono text-[16px] font-bold">{money(rev.avg_one_off)}</span><span className="text-[12.5px] text-[#59554D]"> one-off</span></div>
              ) : (
                <div className="text-[12.5px] text-[#59554D]">Withheld — see Figures withheld below.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demos and what they told us */}
      <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E6E2D9] flex-wrap">
            <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Demos and what they told us</span>
            <span className="text-[11.5px] text-[#59554D]">Each row carries the asks and the objection drawn from that demo's transcript.</span>
            <span className="mono text-[11.5px] text-[#59554D] ml-auto">{Math.min(demos.length, data.demos.length)} of {data.demos.length}</span>
          </div>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 1160 }}>
              <div className="grid items-center px-4 h-9 bg-[#F6F4EF] border-b border-[#DAD5CA]" style={{ gridTemplateColumns: "minmax(210px,1.3fr) 104px 150px 130px minmax(200px,1.1fr) minmax(230px,1.4fr)", columnGap: 12 }}>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#1A1A1A] font-bold">Company</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Demo date</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Outcome</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium text-right">Quote</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Asked for</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Objection raised</span>
              </div>
              {data.demos.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px] font-semibold text-[#8A857A]" data-testid="so-demos-empty">No demos completed in this window.</div>
              ) : demos.map((d) => (
                <div key={d.prospect_id} className="grid items-center px-4 border-b border-[#F0EDE6]" style={{ gridTemplateColumns: "minmax(210px,1.3fr) 104px 150px 130px minmax(200px,1.1fr) minmax(230px,1.4fr)", columnGap: 12, minHeight: 56, padding: "10px 16px" }} data-testid={`so-demo-${d.prospect_id}`}>
                  <div className="min-w-0">
                    <button onClick={() => navigate(`/prospects/${d.prospect_id}`)} className="text-[13px] font-semibold text-[#8A6D00] hover:underline truncate block text-left w-full">{d.company_name}</button>
                    {d.demo_title && <div className="text-[11.5px] text-[#59554D] truncate">{d.demo_title}</div>}
                  </div>
                  <div className="mono text-[11.5px] text-[#59554D]">{formatDate(d.demo_date)}</div>
                  <div><OutcomePill status={d.outcome} /></div>
                  <div className="text-right">
                    {d.quote ? (
                      <div>
                        {d.quote.monthly > 0 && <div className="mono text-[12.5px] font-bold text-[#1A1A1A]">{money(d.quote.monthly)}<span className="text-[#59554D] font-normal">/mo</span></div>}
                        {d.quote.one_off > 0 && <div className="mono text-[11px] text-[#59554D]">{d.quote.monthly > 0 ? "plus " : ""}{money(d.quote.one_off)} one-off</div>}
                        {d.quote.reason && <div className="text-[11px] text-[#59554D]">{d.quote.reason}</div>}
                      </div>
                    ) : (
                      <div><span className="mono text-[12.5px] text-[#B5AFA3]">—</span><div className="text-[11px] text-[#8A857A]">no quote yet</div></div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(d.asked_for || []).length ? d.asked_for.map((t) => (
                      <span key={t} className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#F1EEE6", color: "#59554D" }}>{t}</span>
                    )) : <span className="text-[11.5px] text-[#B5AFA3]">—</span>}
                  </div>
                  <div className="text-[12.5px] leading-[1.45] text-[#59554D]" style={{ textWrap: "pretty" }}>
                    {d.has_transcript ? (d.objection || <span className="text-[#B5AFA3]">—</span>) : <span className="italic text-[#59554D]">No transcript uploaded</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {data.demos.length > 5 && (
            <div className="flex items-center justify-between px-4 py-2.5 bg-[#FBFAF7] border-t border-[#E6E2D9]">
              <span className="text-[11.5px] text-[#59554D]">Asks and objections are generated from the recording transcript once it is uploaded.</span>
              <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)} className="h-7 bg-white border-[#C9C3B6] text-[#59554D] hover:bg-[#F4F2ED] text-[12px]" data-testid="so-demos-all">{showAll ? "Show fewer" : `All ${data.demos.length} demos`}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Where deals are sitting now */}
      {data.sub_status_breakdown && (
        <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }} data-testid="so-substatus">
          <div className="px-4 py-3 border-b border-[#E6E2D9]">
            <div className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Where deals are sitting now</div>
            <div className="text-[11.5px] text-[#59554D] mt-0.5">A live snapshot of the open pipeline by sub-status, so you can see where deals are stalling — not bound to the date window above.</div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", background: "#E6E2D9", gap: 1 }}>
            {subCol("To demo", data.sub_status_breakdown.to_demo)}
            {subCol("To client", data.sub_status_breakdown.to_client)}
          </div>
        </Card>
      )}

      {/* Figures withheld */}
      {data.withheld.length > 0 && (
        <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }} data-testid="so-withheld">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: "#FFE9E0", borderBottom: "1px solid #F3BFA8" }}>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#C23A00" }} />
              <span className="text-[14px] font-bold" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>Figures withheld</span>
              <span className="text-[11.5px]" style={{ color: "#9A3200" }}>{data.withheld.length === 1 ? "One number cannot be stated honestly yet." : `${data.withheld.length} numbers cannot be stated honestly yet.`} Each is named here rather than printed as a headline.</span>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", background: "#E6E2D9", gap: 1 }}>
              {data.withheld.map((w, i) => (
                <div key={i} className="bg-white p-4">
                  <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1">{w.overline}</div>
                  <div className="text-[13.5px] font-semibold text-[#1A1A1A]">{w.verdict}</div>
                  <div className="text-[12.5px] leading-[1.5] text-[#59554D] mt-1">{w.explanation}</div>
                  <div className="mono text-[11px] mt-2" style={{ color: "#9A3200" }}>{w.blocking}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
